import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Tenant } from '@hyvay/shared-types';
import { tenantService } from '../../services/tenant.service';
import { templateService, type Template } from '../../services/template.service';
import { trackAppCreation, trackTemplateUse } from '../../components/analytics/GoogleAnalytics';
import type { BootCopy } from './BootOverlay';

/**
 * "Use this" on a template: create a project, copy the template's files into it
 * and open the studio on its preview (no AI turn, no credit). Shared by the
 * landing's template section and /templates.
 */
export function useApplyTemplate(opts: {
    isAuthenticated: boolean;
    launch: (copy: BootCopy) => void;
    abortLaunch: () => void;
    /** The project was created but the template could not be applied. */
    onCreatedEmpty?: (tenant: Tenant) => void;
}) {
    const navigate = useNavigate();
    const [applying, setApplying] = useState<string | null>(null);

    const apply = async (template: Template) => {
        if (!opts.isAuthenticated) { navigate('/login'); return; }
        if (applying) return;
        setApplying(template.id);
        opts.launch('BUILDING YOUR SITE');

        const created = await tenantService.createTenant({ name: template.name, description: template.description });
        if (!created.success || !created.data) { opts.abortLaunch(); setApplying(null); return; }

        const appId = created.data.id;
        trackAppCreation('template');
        trackTemplateUse(template.id);
        const applied = await templateService.apply(appId, template.id);
        if (!applied.success) {
            // The app exists but has no template files: keep the user here with it visible.
            opts.abortLaunch();
            opts.onCreatedEmpty?.(created.data);
            setApplying(null);
            return;
        }
        // No seed prompt: the template IS the site. The studio opens on its preview
        // straight away (no AI turn, no credit) and the user prompts what to change.
        window.location.assign(`/studio/${appId}`);
    };

    return { applying, apply };
}
